import { AuctionBatchInfo, StoreInfo, PickUpInfo, RecipientInfo } from '@/components/shared';
import { ROUTE_PATHS } from '@/constants';
import { useAppDispatch, useAppSelector, useAuth, useUser } from '@/hooks';
import { pathJoin } from '@/utils';
import { Flex, Heading } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuoteSummary } from '../../hooks';
import { createQuote, updatePrivacy } from '../../store';
import type { ISummaryFormFields } from '../../types';
import type { IMBEStore, IUser } from '@/types';
import { SummaryBox } from './SummaryBox';
import { SummaryForm } from './SummaryForm';
import { updateUser } from '@/store/userSlice';

export const StepForm = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { authUser } = useAuth();
  const userDatabase = useUser();
  const shippingInfo = useAppSelector((state) => state.quote.shippingInfo);

  const isLogged = authUser?.isValid();

  let userData: IUser | undefined;

  const storeInfo: IMBEStore | undefined = shippingInfo?.store;

  const storeData = {
    city: storeInfo?.city ?? '',
    countryCode: storeInfo?.countryCode ?? '',
    email: storeInfo?.email ?? '',
    name: storeInfo?.name ?? '',
    phoneNumber: storeInfo?.phoneNumber ?? '',
    streetAddress: storeInfo?.streetAddress ?? '',
    zipCode: storeInfo?.zipCode ?? '',
  };

  if (isLogged) {
    userData = userDatabase;
  }

  const { defaultValues, ...summaryData } = useAppSelector(({ quote, users }) => ({
    user: isLogged ? userData : users.user,
    auctionHouse: quote.auctionHouse,
    shipping: quote.shippingInfo,
    defaultValues: quote.privacy,
  }));

  const {
    auctionCode,
    auctionDate,
    auctionHouseAddress,
    auctionName,
    batchCode,
    shippingAddress,
    shippingRecipient,
  } = useQuoteSummary({ ...summaryData, item: summaryData.auctionHouse?.item });

  const isBatchVisible = Boolean(batchCode || auctionCode);

  const handleSubmit = async (data: ISummaryFormFields): Promise<void> => {
    dispatch(updatePrivacy(data));

    try {
      if (isLogged && userData) {
        dispatch(updateUser(userData));
      }

      const res = await dispatch(createQuote()).unwrap();

      const path =
        res.status === 'QUOTE_PENDING'
          ? `/details/${res.id}`
          : pathJoin([`/${ROUTE_PATHS.quote}`, ROUTE_PATHS.quoteRequestedConfirmation]);

      navigate(path, { state: { returnPath: 'false' }, replace: true });
    } catch (rejectedValueOrSerializedError) {
      console.log(rejectedValueOrSerializedError);
    }
  };

  const handleBack = (): void => {
    const path = pathJoin([`/${ROUTE_PATHS.quote}`, ROUTE_PATHS.quoteShippingInfo]);
    navigate(path);
  };

  return (
    <Flex direction="column" flexBasis={{ base: 'full', md: 350, xl: 450 }} gap={12}>
      <SummaryBox>
        <RecipientInfo {...shippingAddress} fullname={shippingRecipient} />
        <PickUpInfo {...auctionHouseAddress} ahName={auctionName} />
        {isBatchVisible && (
          <AuctionBatchInfo
            auctionCode={auctionCode}
            auctionDate={auctionDate}
            batchCode={batchCode}
          />
        )}
        {storeInfo && <StoreInfo store={storeData} />}
      </SummaryBox>
      <Heading fontSize="large" fontWeight="bold">
        {t('WIZARD.SUMMARY.FORM_HEADER')}
      </Heading>
      <SummaryForm
        defaultValues={defaultValues}
        onBack={handleBack}
        onSubmit={(data): void => void handleSubmit(data)}
      />
    </Flex>
  );
};
